import * as React from 'react';
import { useState } from 'react';
import Menu from '@mui/icons-material/Menu';
import { Box, Button, styled, Toolbar } from '@mui/material';
import AppBar from '@mui/material/AppBar';
import useTranslation from 'next-translate/useTranslation';
import Link from 'next/link';
import useSiteContext from 'src/store/slices/site';
import theme from 'src/themes';
import useUserContext from 'src/store/slices/user';
import LanguageSelectorMenu from '../LanguageSelectorMenu';
import SkipContentLink from '../SkipContentLink';
import AccountMenu from '../AccountMenu';
import { MobileNavigation, DesktopNavigation } from './HeaderNavigation';
import { ReciteMeReader } from '../ScreenReaders';

type Props = {
  hideNavigation?: boolean;
};

const Logo = styled('img')(() => ({
  maxHeight: '55px',
  maxWidth: '100%',
  padding: theme.spacing(1),
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  [theme.breakpoints.up('lg')]: {
    padding: 0,
  },
}));

const HeaderToolbar = styled(Toolbar)(() => ({
  maxWidth: theme.breakpoints.values.lg,
  margin: 'auto',
  width: '100%',
  '&.MuiToolbar-root': {
    padding: 0,
  },
  justifyContent: 'space-between',
}));

export const Header = ({ hideNavigation }: Props) => {
  const {
    siteNavigation,
    logoUrl,
    authenticationSettings,
    languagesSupported,
  } = useSiteContext();
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);

  const { t } = useTranslation();
  const { user } = useUserContext();

  const handleDrawerToggle = () => {
    setIsMobileDrawerOpen(!isMobileDrawerOpen);
  };

  const userInitials =
    user?.firstName && user?.lastName
      ? `${user?.firstName?.charAt(0).toUpperCase()}${user?.lastName
          ?.charAt(0)
          .toUpperCase()}`
      : '';

  const isAuthenticationEnabled = authenticationSettings?.isEnabled === true;

  const shouldShowLoginLink =
    !user && isAuthenticationEnabled && !hideNavigation;

  const hasSiteNavigationItems = siteNavigation && siteNavigation.length > 0;
  const isUserLoggedIn = user && isAuthenticationEnabled;
  const shouldShowUserMenu = isUserLoggedIn;
  const hasMultipleLanguages =
    languagesSupported && languagesSupported?.length > 1;

  const shouldShowNavLinks =
    (hasSiteNavigationItems && isUserLoggedIn) ||
    (!isAuthenticationEnabled && !hideNavigation);

  const shouldShowMobileMenu =
    shouldShowLoginLink ||
    shouldShowUserMenu ||
    shouldShowNavLinks ||
    hasMultipleLanguages;

  return (
    <>
      <AppBar data-testid="header-container">
        <SkipContentLink
          href="#main-content"
          label="header.skipNavigationLink"
        />
        <HeaderToolbar>
          {logoUrl && (
            <Link href="/" title={t('header.homeLinkLogoTitle')}>
              <Logo src={logoUrl} alt="" data-testid="header-logo" />
            </Link>
          )}
          {shouldShowNavLinks && (
            <DesktopNavigation siteNavigation={siteNavigation} />
          )}

          <Box
            sx={{
              display: {
                xs: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                marginLeft: theme.spacing(2),
              },
            }}
          >
            <Box
              sx={{
                display: {
                  xs: 'none',
                  md: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  marginLeft: theme.spacing(2),
                },
              }}
            >
              <>
                {shouldShowLoginLink && (
                  <>
                    <Button
                      data-testid="login-button-desktop"
                      LinkComponent={Link}
                      variant="contained"
                      href="/login"
                      sx={{
                        height: '100%',
                      }}
                    >
                      {t('user:login.loginButton')}
                    </Button>
                  </>
                )}

                <LanguageSelectorMenu />
                <ReciteMeReader />
                {shouldShowUserMenu && (
                  <AccountMenu avatarText={userInitials} />
                )}
              </>
            </Box>

            {shouldShowMobileMenu && (
              <Button
                color="primary"
                aria-label="open drawer"
                onClick={handleDrawerToggle}
                sx={{ ml: 2, display: { md: 'none' }, fontSize: '0.75rem' }}
                startIcon={<Menu />}
                data-testid="menu-button-mobile"
              >
                {t('header.mobileMenuLabel')}
              </Button>
            )}
          </Box>
        </HeaderToolbar>
      </AppBar>
      {shouldShowMobileMenu && (
        <MobileNavigation
          handleDrawerToggle={handleDrawerToggle}
          shouldShowNavLinks={shouldShowNavLinks}
          shouldShowUserMenu={shouldShowUserMenu}
          shouldShowLoginLink={shouldShowLoginLink}
          isMobileDrawerOpen={isMobileDrawerOpen}
          siteNavigation={siteNavigation}
        />
      )}
    </>
  );
};

export default Header;
